import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Fragment, useMemo, useState } from "react";
import { z } from "zod";
import { fallback } from "@tanstack/zod-adapter";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAuditEvents } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, Download, RefreshCw } from "lucide-react";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

const auditSearchSchema = dateRangeSearchSchema.extend({
  entity: fallback(z.string().optional(), undefined),
  q: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/_authenticated/app/admin/audit")({
  validateSearch: zodValidator(auditSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: AuditViewer,
});

const ENTITIES = [
  "all", "orders", "order", "shipments", "shipment",
  "asn", "asn_line", "receipt", "receipt_photo",
  "inventory_lots", "lot_holds", "serials", "allocations",
  "putaway_rules", "replenishment_rules", "print_job", "inventory_adjustment",
];

function toCsv(rows: any[]) {
  const head = ["when", "entity_type", "action", "entity_id", "actor_id", "diff"];
  const esc = (v: unknown) => {
    const s = v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((e) => [
    e.created_at, e.entity_type, e.action, e.entity_id, e.actor_id ?? "", e.diff ?? "",
  ].map(esc).join(","));
  return [head.join(","), ...body].join("\n");
}

function AuditViewer() {
  const { tenantId } = useScope();
  const search = Route.useSearch();
  type S = typeof search;
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: Partial<S>) =>
    navigate({ search: (prev: S) => ({ ...prev, ...patch }) });
  const entity = search.entity ?? "all";
  const q = search.q ?? "";
  const sort = search.sort ?? "desc";
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const fetchEvents = useServerFn(listAuditEvents);

  // Default window: last 24h if user hasn't picked one
  const effectiveStart = search.start
    ?? (search.end ? undefined : new Date(Date.now() - 86400000).toISOString().slice(0, 10));
  const effectiveEnd = search.end;
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: effectiveEnd }),
    [effectiveStart, effectiveEnd],
  );

  const { data: events = [], isLoading, refetch } = useQuery({
    queryKey: ["audit-events", tenantId, entity, fromIso, toIso, sort],
    enabled: !!tenantId,
    queryFn: () => runServerFn(
      () => fetchEvents({ data: { tenantId: tenantId!, entity, fromIso, toIso, limit: 1000 } }),
      "Loading audit",
    ),
  });

  const filtered = q
    ? events.filter((e: any) =>
        (e.entity_id ?? "").toLowerCase().includes(q.toLowerCase())
        || (e.actor_id ?? "").toLowerCase().includes(q.toLowerCase())
        || (e.action ?? "").toLowerCase().includes(q.toLowerCase()),
      )
    : events;

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a: any, b: any) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sort === "asc" ? ta - tb : tb - ta;
    });
    return copy;
  }, [filtered, sort]);

  const actionVariant = (a: string): "default" | "secondary" | "destructive" =>
    /delete|cancel|remove|fail|recall|quarantine/i.test(a) ? "destructive"
      : /create|add|insert|allocate|ship|receive|release/i.test(a) ? "default"
      : "secondary";

  const downloadCsv = () => {
    const blob = new Blob([toCsv(sorted)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Audit Log</h2>
          <p className="text-sm text-muted-foreground">
            Tenant-wide changes across orders, lots, shipments, and rules. Click a row to inspect the diff.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={sorted.length === 0}>
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Select
          value={entity}
          onValueChange={(v) => update({ entity: v === "all" ? undefined : v })}
        >
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ENTITIES.map((e) => <SelectItem key={e} value={e}>{e === "all" ? "All entities" : e}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangeFilter
          value={{ start: search.start, end: search.end }}
          onChange={(next) => update({ start: next.start, end: next.end })}
        />
        <SortDirectionToggle
          value={sort}
          onChange={(s) => update({ sort: s === "desc" ? undefined : s })}
        />
        <Input
          placeholder="Filter by action, entity id, or actor…"
          value={q}
          onChange={(e) => update({ q: e.target.value || undefined })}
          className="max-w-md"
        />
        {(search.start || search.end || search.entity || search.q || search.sort) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({ search: () => ({}) })
            }
          >
            Clear all
          </Button>
        )}
        <div className="text-xs text-muted-foreground self-center ml-auto">
          {sorted.length} of {events.length} shown
        </div>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8"></TableHead>
            <TableHead>When</TableHead>
            <TableHead>Entity</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Entity id</TableHead>
            <TableHead>Actor</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : sorted.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                {(search.start || search.end) ? (
                  <span>
                    No events in this range.{" "}
                    <button
                      className="underline"
                      onClick={() => update({ start: undefined, end: undefined })}
                    >
                      Show all time
                    </button>
                  </span>
                ) : "No events."}
              </TableCell></TableRow>
            ) : sorted.map((e: any) => {
              const isOpen = !!expanded[e.id];
              const hasDiff = e.diff && (typeof e.diff !== "object" || Object.keys(e.diff).length > 0);
              return (
                <Fragment key={e.id}>
                  <TableRow
                    className={hasDiff ? "cursor-pointer hover:bg-muted/40" : ""}
                    onClick={() => hasDiff && setExpanded((m) => ({ ...m, [e.id]: !isOpen }))}
                  >
                    <TableCell className="text-muted-foreground">
                      {hasDiff ? (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(e.created_at), "MMM d HH:mm:ss")}
                    </TableCell>
                    <TableCell className="text-sm">{e.entity_type}</TableCell>
                    <TableCell><Badge variant={actionVariant(e.action)}>{e.action}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{(e.entity_id ?? "").slice(0, 8)}…</TableCell>
                    <TableCell className="font-mono text-xs">{e.actor_id ? e.actor_id.slice(0, 8) + "…" : "system"}</TableCell>
                  </TableRow>
                  {isOpen && hasDiff && (
                    <TableRow>
                      <TableCell colSpan={6} className="bg-muted/30">
                        <pre className="text-xs font-mono p-3 overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(e.diff, null, 2)}
                        </pre>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
